interface CacheEntry<T> {
  data: T;
  timestamp: number;
  promise?: Promise<T>;
}

class APICache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes
  private readonly maxSize = 50; // Smaller cache size for safety

  /**
   * Get cached data or execute the fetch function
   * @param key - Cache key
   * @param fetchFn - Function to fetch data if not in cache
   * @param ttl - Time to live in milliseconds (default: 5 minutes)
   * @returns Promise with the data
   */
  async get<T>(
    key: string, 
    fetchFn: () => Promise<T>, 
    ttl: number = this.defaultTTL
  ): Promise<T> {
    const now = Date.now();
    const cached = this.cache.get(key);

    // Return cached data if it's still valid
    if (cached && (now - cached.timestamp) < ttl) {
      return cached.data;
    }

    // Return ongoing promise if request is in flight (deduplication)
    if (cached?.promise) {
      return cached.promise;
    }

    // Execute fetch function and cache the promise to prevent duplicate requests
    const promise = fetchFn().then(data => {
      this.set(key, data);
      return data;
    }).catch(error => {
      // Remove failed promise from cache
      this.delete(key);
      throw error;
    });

    // Store the promise temporarily to prevent duplicate requests
    this.cache.set(key, {
      data: null,
      timestamp: now,
      promise
    });

    return promise;
  }

  /**
   * Set data in cache
   * @param key - Cache key
   * @param data - Data to cache
   */
  set<T>(key: string, data: T): void {
    // Implement simple LRU eviction if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Delete entry from cache
   * @param key - Cache key
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Delete all entries that start with the given prefix
   * @param prefix - Key prefix to match
   */
  deleteByPrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.defaultTTL) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Create a singleton instance
export const apiCache = new APICache();

// Utility function to create cache keys
export const createCacheKey = (endpoint: string, params?: Record<string, any>) => {
  if (!params) return endpoint;
  const sortedParams = Object.keys(params).sort().map(key => `${key}:${params[key]}`);
  return `${endpoint}?${sortedParams.join('&')}`;
};

// Clean up expired entries periodically (only in browser)
if (typeof window !== 'undefined') {
  setInterval(() => {
    apiCache.clearExpired();
  }, 60000); // Every minute
}
